import React from 'react';
import { motion } from 'framer-motion';

interface ToggleProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  label?: string;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export const Toggle: React.FC<ToggleProps> = ({
  enabled,
  onChange,
  label,
  disabled = false,
  size = 'md'
}) => {
  const sizes = {
    sm: {
      switch: 'w-8 h-5',
      toggle: 'w-3 h-3',
      translateX: 12 // 3 * 4px (tailwind spacing)
    },
    md: {
      switch: 'w-11 h-6',
      toggle: 'w-4 h-4',
      translateX: 20 // 5 * 4px (tailwind spacing)
    },
    lg: {
      switch: 'w-14 h-7',
      toggle: 'w-5 h-5',
      translateX: 28 // 7 * 4px (tailwind spacing)
    }
  };

  const currentSize = sizes[size];

  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={label || 'Toggle'}
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      className={`
        ${currentSize.switch}
        relative inline-flex items-center rounded-full
        transition-colors duration-200 ease-in-out
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:focus:ring-offset-gray-800
        ${enabled 
          ? 'bg-purple-600 dark:bg-purple-500' 
          : 'bg-gray-200 dark:bg-gray-700'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      <span className="sr-only">{label || 'Toggle'}</span>
      <motion.span
        animate={{
          x: enabled ? currentSize.translateX : 4 // 4px = 1 * 4px (initial offset)
        }}
        transition={{
          type: "spring",
          stiffness: 500,
          damping: 30
        }}
        className={`
          ${currentSize.toggle}
          inline-block rounded-full bg-white shadow-lg
        `}
      />
    </button>
  );
};

export default Toggle;
